/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable no-console */
import SelectorEngine from '../../../utils/dom/selector-engine';
import Manipulator from '../../../utils/dom/manipulator';
import Request from '../../../utils/request';
import Pagination from '../../../components/Pagination';

const TAB_KEYS = {
	ACTIVE: 'active',
	COMPLETED: 'completed',
	CANCELED: 'canceled',
};
const TAB_HASHES = [TAB_KEYS.ACTIVE, TAB_KEYS.COMPLETED, TAB_KEYS.CANCELED];

const _formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const TABLE_COLUMNS = [
	{ key: 'startDate', label: 'Start date', sortable: true },
	{ key: 'endDate', label: 'End date', sortable: true },
	{ key: 'courseName', label: 'Course name', sortable: true },
	{ key: 'venueName', label: 'Venue', sortable: true },
	{ key: 'totalBooked', label: 'Booked', sortable: true },
	{ key: 'totalWaiting', label: 'Waiting', sortable: true },
	{ key: 'status', label: 'Status', sortable: true }
];

const _statusTagClass = (status) =>
	status === 'Accepting Bookings'
	? 'lta-tag--success'
	: status === 'Hidden'
	? 'lta-tag--inactive'
	: status === 'Cancelled'
	? 'lta-tag--lta-dark-blue'
	: status === 'Completed'
	? 'lta-tag--success'
	: status === 'In Progress'
	? 'lta-tag--lta-blue'
	: status === 'Awaiting Results'
	? 'lta-tag--danger'
	: 'lta-tag--lta-dark-blue';

class CourseProviders {
	constructor() {
		this._wrapper = SelectorEngine.findOne('.js-course-providers-wrapper');
		if (!this._wrapper) return;

		this._classesApiUrl = Manipulator.getDataAttribute(this._wrapper, 'classes-api-url');
		this._classDetailsPageUrl = Manipulator.getDataAttribute(this._wrapper, 'class-details-page-url');
		this._pageSize = Number(Manipulator.getDataAttribute(this._wrapper, 'page-size')) || 20;

		this._activeTab = null;
		this._cache = {};

		this._tabsWrapper = SelectorEngine.findOne('.js-course-providers-tabs');
		this._tabContainers = this._tabsWrapper.querySelectorAll('.js-course-providers-tab-container');
		this._tabList = this._wrapper.querySelector('.js-course-providers-tab-list');
		this._tabs = this._wrapper.querySelectorAll('.js-course-providers-tab');
		this._panels = this._wrapper.querySelectorAll('.js-course-providers-tab-panel');
		this._tabList.addEventListener('keydown', (e) => this._handleTabKeydown(e));
		this._tabs.forEach((tab, index) => {
			tab.addEventListener('click', () => {
				this._selectTab(index);
				this._addUrlHash(`${TAB_HASHES[index]}`);
				this._loadTabData();
			});
		});

		this._paginationDOM = {};
		this._tabContainers.forEach((container) => {
			const tabKey = container.dataset.tab;
			const paginationEl = SelectorEngine.findOne('.js-course-providers-pagination-wrapper', container);
			if (!paginationEl) return;
			const paginationInstance = new Pagination(paginationEl);
			paginationInstance.onChange((page) => {
				const pageIndex = Number(page);
				if (Number.isNaN(pageIndex) || pageIndex === this._cache[tabKey]?.paginationData?.pageIndex) return;
				this._pagination = { pageIndex };
				this._updatePaginationUI()
				this._fetchClasses();
			});
			this._paginationDOM[tabKey] = paginationInstance;
		});

		this._showAllBtns = this._wrapper.querySelectorAll('.js-course-providers-show-all');
		this._backTopBtns = this._wrapper.querySelectorAll('.js-course-providers-back-top');
		this._showAllBtns.forEach((btn) => {
			btn.addEventListener('click', async () => {
				this._pagination = { pageIndex: 0, isShowAll: true };
				const initialShowAllText = btn.innerText;
				btn.innerText = 'Fetching courses...';
				await this._fetchClasses();
				btn.innerText = initialShowAllText;
			});
		});
		this._backTopBtns.forEach((btn) => {
			btn.addEventListener('click', async () => {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			});
		});

		this._activateTabFromHash();
		window.addEventListener('hashchange', () => this._activateTabFromHash());
		this._loadTabData();
	}

	get _filters() {
		return this._cache[this._activeTab]?.filters ?? {startDate: '', endDate: '', courseName: '', venueName: '', totalBooked: '', totalWaiting: '', status: ''};
	}

	set _filters(value) {
		if (!this._cache[this._activeTab]) this._cache[this._activeTab] = {};
		this._cache[this._activeTab].filters = { ...this._filters, ...value };
	}

	get _sort() {
		return this._cache[this._activeTab]?.sort ?? { key: null, dir: null };
	}

	set _sort(value) {
		if (!this._cache[this._activeTab]) this._cache[this._activeTab] = {};
		this._cache[this._activeTab].sort = value;
	}

	get _pagination() {
		return this._cache[this._activeTab]?.paginationData ?? { pageIndex: 0, totalItems: 0, isShowAll: false };
	}

	set _pagination(value) {
		if (!this._cache[this._activeTab]) this._cache[this._activeTab] = {};
		this._cache[this._activeTab].paginationData = { ...this._pagination, ...value };
	}

	get _classes() {
		return this._cache[this._activeTab]?.classes ?? [];
	}

	set _classes(value) {
		if (!this._cache[this._activeTab]) this._cache[this._activeTab] = {};
		this._cache[this._activeTab].classes = value;
	}

	_addUrlHash(hash) {
		if (window.location.hash === hash) return;
		window.location.hash = hash;
	}

	_handleTabKeydown(e) {
		const tabs = [...this._tabs];
		const current = tabs.indexOf(document.activeElement);
		if (current === -1) return;

		let next;
		switch (e.key) {
			case 'ArrowRight':
				next = (current + 1) % tabs.length;
				break;
			case 'ArrowLeft':
				next = (current - 1 + tabs.length) % tabs.length;
				break;
			case 'Home':
				next = 0;
				break;
			case 'End':
				next = tabs.length - 1;
				break;
			default:
				return;
		}
		e.preventDefault();
		tabs[next].focus();
		this._selectTab(next);
		this._addUrlHash(`${TAB_HASHES[next]}`);
		this._loadTabData();
	}

	_selectTab(index) {
		this._activeTab = TAB_HASHES[index];
		this._tabs.forEach((tab, i) => {
			const selected = i === index;
			tab.setAttribute('aria-selected', selected ? 'true' : 'false');
			tab.tabIndex = selected ? 0 : -1;
			tab.classList.toggle('active', selected);
		});
		this._panels.forEach((panel, i) => {
			panel.hidden = i !== index;
			panel.classList.toggle('active', i === index);
		});
	}

	_activateTabFromHash() {
		if (!window.location.hash) {
			this._addUrlHash(`${TAB_HASHES[0]}`);
		}
		const hash = window.location.hash.replace('#', '');
		const index = TAB_HASHES.indexOf(hash);
		if (index === -1) return;
		this._selectTab(index);
	}

	_getActiveContainer() {
		return [...this._tabContainers].find((el) => el.dataset.tab === this._activeTab);
	}

	_applyFilters(classes) {
		const { startDate, endDate, courseName, venueName, totalBooked, totalWaiting, status } = this._filters;
		if (!startDate && !endDate && !courseName && !venueName && !totalBooked && !totalWaiting && !status) return classes;
		return classes.filter((c) => {
			if (endDate && c.endDate && !c.endDate.startsWith(endDate)) return false;
			if (startDate && c.startDate && !c.startDate.startsWith(startDate)) return false;
			if (courseName && !(c.courseName || '').toLowerCase().includes(courseName.toLowerCase())) return false;
			if (venueName && !(c.venueName || '').toLowerCase().includes(venueName.toLowerCase())) return false;
			if (totalBooked && !String(c.totalBooked).includes(totalBooked)) return false;
			if (totalWaiting && !String(c.totalWaiting).includes(totalWaiting)) return false;
			if (status && c.status !== status) return false;
			return true;
		});
	}

	_sortIndicator(key) {
		if (this._sort.key !== key || !this._sort.dir) return '';
		return this._sort.dir === 'asc' ? ' ▲' : ' ▼';
	}

	_loadTabData() {
		if (this._classes.length) {
			this._updateCoursesUI();
			this._updateTabCountsUI();
			this._updatePaginationUI();
			this._updateShowAllUI();
			this._updateFiltersUI();
			return;
		}
		this._pagination = { pageIndex: 0 };
		this._fetchClasses();
	}

	_updateTabCountsUI() {
		const container = this._getActiveContainer();
		const title = container.querySelector('.js-course-providers-tab-title');
		if (!container || !title) return;
		const titleVariants = {
			[TAB_KEYS.ACTIVE]: 'Active courses',
			[TAB_KEYS.COMPLETED]: 'Completed courses',
			[TAB_KEYS.CANCELED]: 'Canceled courses'
		};
		if (title) title.textContent = `${titleVariants[this._activeTab]} (${this._pagination.totalItems})`;
	}

	_updateFiltersUI() {
		if (!this._pagination.isShowAll) return;

		const container = this._getActiveContainer();
		const filtersContainer = container?.querySelector('.js-course-providers-filters');
		if (!filtersContainer) return;

		const classes = this._classes;
		const statusOptions = [...new Set(classes.map((c) => c.status).filter(Boolean))]
			.map((s) => `<option value="${s}" ${this._filters.status === s ? 'selected' : ''}>${s}</option>`)
			.join('');
		const startDateOptions = [...new Set(classes.map((c) => c.startDate?.slice(0, 10)).filter(Boolean))]
			.sort()
			.map((d) => `<option value="${d}" ${this._filters.startDate === d ? 'selected' : ''}>${_formatDate(d)}</option>`)
			.join('');
		const endDateOptions = [...new Set(classes.map((c) => c.endDate?.slice(0, 10)).filter(Boolean))]
			.sort()
			.map((d) => `<option value="${d}" ${this._filters.endDate === d ? 'selected' : ''}>${_formatDate(d)}</option>`)
			.join('');

		filtersContainer.innerHTML = `
			<div class="u-flex u-flex-column md:u-flex-row u-gap-1 u-mb-3" role="search" aria-label="Filter classes">
				<select class="p-course-providers-course-details__filter-input" data-filter="startDate" aria-label="Filter by start date">
					<option value="">Any start date</option>
					${startDateOptions}
				</select>
				<select class="p-course-providers-course-details__filter-input" data-filter="endDate" aria-label="Filter by end date">
					<option value="">Any end date</option>
					${endDateOptions}
				</select>
				<input type="text" class="p-course-providers-course-details__filter-input" data-filter="courseName" aria-label="Filter by name" placeholder="Any course" value="${this._filters.courseName}" />
				<input type="text" class="p-course-providers-course-details__filter-input" data-filter="venueName" aria-label="Filter by venue" placeholder="Any venue" value="${this._filters.venueName}" />
				<input type="text" class="p-course-providers-course-details__filter-input" data-filter="totalBooked" aria-label="Filter by booked" placeholder="Booked" value="${this._filters.totalBooked}" />
				<input type="text" class="p-course-providers-course-details__filter-input" data-filter="totalWaiting" aria-label="Filter by waiting" placeholder="Waiting" value="${this._filters.totalWaiting}" />
				<select class="p-course-providers-course-details__filter-input" data-filter="status" aria-label="Filter by status">
					<option value="">Any status</option>
					${statusOptions}
				</select>
			</div>`;

		filtersContainer.querySelectorAll('[data-filter]').forEach((input) => {
			const handler = () => {
				this._filters = {[input.dataset.filter]: input.value};
				this._updateCoursesUI();
			};
			input.addEventListener('input', handler);
			input.addEventListener('change', handler);
		});
	}

	_updatePaginationUI() {
		const container = this._getActiveContainer();
		const paginationContainer = container?.querySelector('.js-course-providers-pagination-wrapper');
		const paginationInstance = this._paginationDOM[this._activeTab];
		if (!paginationContainer || !paginationInstance) return;
		const { totalItems, pageIndex, isShowAll } = this._pagination;
		const pageSize = isShowAll && totalItems ? totalItems : this._pageSize;
		const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
		paginationContainer.hidden = totalPages <= 1;
		paginationInstance.data = { totalPages, currentPage: pageIndex };
	}

	_updateCoursesUI() {
		const container = this._getActiveContainer();
		const body = container?.querySelector('.js-course-providers-tab-body');
		const status = container.querySelector('.js-course-providers-tab-status');
		if (!body) return;

		const processedClasses = this._applyFilters(this._classes);

		if (!processedClasses?.length) {
			body.innerHTML = '';
			status.innerText = 'No courses found';
			status.hidden = false;
			return;
		} else {
			status.hidden = true;
		}

		const headers = TABLE_COLUMNS.map((col) =>
			col.sortable
				? `<th style="cursor:pointer" data-sort-key="${col.key}" aria-sort="${this._sort.key === col.key && this._sort.dir === 'asc' ? 'ascending' : this._sort.key === col.key && this._sort.dir === 'desc' ? 'descending' : 'none'}">${col.label}${this._sortIndicator(col.key)}</th>`
				: `<th>${col.label}</th>`
		).join('');

		const rows = processedClasses.map((c) => `
			<tr style="cursor:pointer" data-class-id="${c.id}">
				<td style="min-width: 125px;">${_formatDate(c.startDate)}</td>
				<td style="min-width: 125px;">${_formatDate(c.endDate)}</td>
				<td><a class='u-p1' href="${this._classDetailsPageUrl}?classId=${c.id}">${c.courseName || ''}</a></td>
				<td>${c.venueName || ''}</td>
				<td style="min-width: 120px;">${c.totalBooked}</td>
				<td style="min-width: 120px;">${c.totalWaiting}</td>
				<td><span class="lta-tag ${_statusTagClass(c.status)}">${c.status || ''}</span></td>
			</tr>`
		).join('');

		body.innerHTML = `
			<div class="p-course-providers__table">
				<table>
					<thead><tr>${headers}</tr></thead>
					<tbody>${rows}</tbody>
				</table>
			</div>
		`;
		body.querySelectorAll('th[data-sort-key]').forEach((th) => {
			th.addEventListener('click', () => {
				const k = th.dataset.sortKey;
				const cycle = { null: 'asc', asc: 'desc', desc: null };
				const nextDir = this._sort.key === k ? cycle[this._sort.dir ?? 'null'] : 'asc';
				this._sort = { key: nextDir ? k : null, dir: nextDir };
				this._pagination = { pageIndex: 0 };
				this._updateCoursesUI();
				this._fetchClasses();
			});
		});
		body.querySelectorAll('tr[data-class-id]').forEach((row) => {
			const navigate = () => { window.location.href = `${this._classDetailsPageUrl}?classId=${row.dataset.classId}`; };
			row.addEventListener('click', navigate);
		});
	}

	_updateFetchingUI(loading = true) {
		const container = this._getActiveContainer();
		if (!container) return;
		const status = container.querySelector('.js-course-providers-tab-status');

		if (loading) {
			if (!this._classes.length) {
				status.innerText = 'Fetching courses...';
				status.hidden = false;
			}
			this._wrapper.classList.toggle('courses-loading');
		} else {
			status.innerText = '';
			status.hidden = true;
			this._wrapper.classList.toggle('courses-loading');
		}
	}

	_updateShowAllUI() {
		const container = this._getActiveContainer();
		const showAllContainer = container?.querySelector('.js-course-providers-show-all-container');
		const backTopContainer = container?.querySelector('.js-course-providers-back-top-container');
		if (!container || !showAllContainer || !backTopContainer) return;
		showAllContainer.hidden = true;
		backTopContainer.hidden = true;
		if (this._pagination.isShowAll) {
			if (this._classes.length) backTopContainer.hidden = false;
		} else {
			if (this._pagination.totalItems > this._classes.length) showAllContainer.hidden = false;
		}
	}

	async _fetchClasses() {
		const { pageIndex, totalItems, isShowAll } = this._pagination;
		try {
			this._updateFetchingUI(true);
			const response = await Request.post(
				this._classesApiUrl,
				{
					classStatus: this._activeTab,
					pageSize: isShowAll ? totalItems : this._pageSize,
					pageIndex: pageIndex,
					filter: this._sort.key,
					order: this._sort.dir
				}
			);
			const { classes, paginationPageIndex, paginationTotalItems } = response.responseContainerContents;
			this._classes = classes;
			this._pagination = { pageIndex: paginationPageIndex, totalItems: paginationTotalItems };
		} catch (error) {
			this._classes = [];
			this._pagination = { pageIndex: 0, totalItems: 0 };
		} finally {
			this._updateFetchingUI(false);
			this._updateCoursesUI();
			this._updateTabCountsUI();
			this._updatePaginationUI();
			this._updateShowAllUI();
			this._updateFiltersUI();
			document.dispatchEvent(new CustomEvent('notifications:ready-to-fetch'));
		}
	}
}

const globalCourseProviders = new CourseProviders();
export default globalCourseProviders;
